import React, { useState, useCallback, useEffect } from 'react'
import { motion } from 'framer-motion'
import { GripVertical, GripHorizontal } from 'lucide-react'

interface ResizeHandleProps {
  direction: 'horizontal' | 'vertical'
  onResize: (delta: number) => void
  className?: string
}

const ResizeHandle: React.FC<ResizeHandleProps> = ({ direction, onResize, className = '' }) => {
  const [isDragging, setIsDragging] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [dragStart, setDragStart] = useState(0)

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true)
    setDragStart(direction === 'horizontal' ? e.clientX : e.clientY)
    document.body.style.cursor = direction === 'horizontal' ? 'col-resize' : 'row-resize'
    document.body.style.userSelect = 'none'
    e.preventDefault()
  }, [direction])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return

    const current = direction === 'horizontal' ? e.clientX : e.clientY
    const delta = current - dragStart
    onResize(delta)
    setDragStart(current)
  }, [isDragging, dragStart, onResize, direction])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }, [])

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  const baseClasses = direction === 'horizontal' 
    ? 'w-1 cursor-col-resize'
    : 'h-1 cursor-row-resize'

  const hoverClasses = isHovered || isDragging
    ? 'bg-[#FE4215] shadow-lg'
    : 'bg-gray-300 dark:bg-gray-600'

  return (
    <motion.div
      className={`${baseClasses} ${className} ${hoverClasses} relative z-20 group transition-all duration-200 flex items-center justify-center`}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: direction === 'horizontal' ? 1.2 : 1 }}
      whileTap={{ scale: direction === 'horizontal' ? 1.1 : 1 }}
      transition={{ duration: 0.1 }}
    >
      {/* Visual grip indicator */}
      <div className={`${isHovered || isDragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-70'} transition-opacity duration-200`}>
        {direction === 'horizontal' ? (
          <GripVertical className="w-3 h-3 text-white drop-shadow-sm" />
        ) : (
          <GripHorizontal className="w-3 h-3 text-white drop-shadow-sm" />
        )}
      </div>
      
      {/* Expanded hover area for easier interaction */}
      <div className={`absolute ${direction === 'horizontal' ? '-left-2 -right-2 top-0 bottom-0' : '-top-2 -bottom-2 left-0 right-0'}`} />
      
      {/* Tooltip on hover */}
      {isHovered && !isDragging && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`absolute z-30 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg pointer-events-none ${
            direction === 'horizontal' 
              ? 'left-2 top-1/2 -translate-y-1/2' 
              : 'top-2 left-1/2 -translate-x-1/2'
          }`}
        >
          Drag to resize
          <div className={`absolute w-0 h-0 ${
            direction === 'horizontal'
              ? 'border-t-2 border-b-2 border-r-4 border-transparent border-r-gray-900 -left-1 top-1/2 -translate-y-1/2'
              : 'border-l-2 border-r-2 border-b-4 border-transparent border-b-gray-900 -top-1 left-1/2 -translate-x-1/2'
          }`} />
        </motion.div>
      )}
      
      {/* Active dragging indicator */}
      {isDragging && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-[#FE4215] shadow-[0_0_20px_rgba(254,66,21,0.4)]"
        />
      )}
    </motion.div>
  )
}

export default ResizeHandle
